import { useMemo, useState } from "react";
import { AddableCourse, CourseLite, CourseSummary } from "../../types/course.response";
import { Requirement, Select, CourseSet } from "../../types/requirement.response";
import { getAllCourseOptionsForRequirement } from "../../utils/requirementResponseUtils";
import RequirementTabBar from "../shared/RequirementTabBar";
import SidebarCourseSetSlots from "./SidebarCourseSetSlots";
import SidebarAddCourse from "./SidebarAddCourse";

interface SidebarOptionGroupSlotsProps {
    rootNode: Select;
    requirement: Requirement;
    coursesMap: Record<string, CourseSummary>;
    onAdd: (course: AddableCourse, nodeId: string | null) => void | Promise<void>;
    onDelete: (course: CourseLite) => void | Promise<void>;
    isBroad?: boolean;
}

export default function SidebarOptionGroupSlots({
    rootNode,
    requirement,
    coursesMap,
    onAdd,
    onDelete,
    isBroad,
}: SidebarOptionGroupSlotsProps) {
    const groups = rootNode.children.filter(
        (child): child is Select => child.type === "SELECT"
    );
    const isFulfilled = rootNode.summary.isFulfilled;

    const [activeTab, setActiveTab] = useState(0);

    const allCourses = useMemo(
        () => getAllCourseOptionsForRequirement(requirement, coursesMap),
        [requirement, coursesMap]
    );

    if (groups.length === 0) return null;

    const activeGroup = groups[activeTab];
    const courseSets = activeGroup.children.filter(
        (child): child is CourseSet => child.type === "COURSE_SET"
    );

    return (
        <div className="mt-3">
            <RequirementTabBar
                items={groups}
                fulfilledIds={rootNode.fulfilledChildIds}
                activeTab={activeTab}
                onTabChange={setActiveTab}
            />

            <div className="mt-3 flex flex-col gap-0.5">
                {courseSets.map(courseSet => (
                    <SidebarCourseSetSlots
                        key={courseSet.id}
                        courseSet={courseSet}
                        coursesMap={coursesMap}
                        requirementId={requirement.info.id}
                        onAdd={(course) => onAdd(course, courseSet.id)}
                        onDelete={onDelete}
                        idPrefix="sidebar-option-group"
                        isBroad={isBroad}
                    />
                ))}
                {isFulfilled && (
                    <SidebarAddCourse
                        allCourses={allCourses}
                        onAdd={(course) => onAdd(course, null)}
                        nodeId={null}
                        dropId={`sidebar-option-group-slot-${requirement.info.id}-extra`}
                        isBroad={isBroad}
                    />
                )}
            </div>
        </div>
    );
}
